import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { DetailHeader } from '../../components/layout/DetailHeader.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FieldGrid } from '../../components/ui/FieldGrid.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { DocumentFlow } from '../../components/data/DocumentFlow.jsx';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate, formatDateTime, formatNumber } from '../../utils/formatters.js';
import { downloadOrderPdf, previewOrderPdf } from '../../utils/pdf.js';
import { formatQty } from '../../utils/units.js';

import { FulfillOrderModal } from './FulfillOrderModal.jsx';
import { OrderSendModal } from './OrderSendModal.jsx';

export function OrderDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);

  const order = useQuery({
    queryKey: ['sales', 'orders', id],
    queryFn: () => salesApi.getOrder(id).then((r) => r.items),
  });
  const customers = useQuery({
    queryKey: ['sales', 'customers'],
    queryFn: () => salesApi.listCustomers().then((r) => r.items || []),
  });
  const deliveries = useQuery({
    queryKey: ['sales', 'orders', id, 'deliveries'],
    queryFn: () => salesApi.listOrderDeliveries(id).then((r) => r.items || []),
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
    retry: false,
  });

  const confirm = useMutation({
    mutationFn: () => salesApi.confirmOrder(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'orders', id] }),
  });
  const cancel = useMutation({
    mutationFn: () => salesApi.cancelOrder(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'orders', id] }),
  });

  if (order.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (order.error || !order.data) {
    return (
      <section className="page">
        <DetailHeader backTo="/sales/orders" title={t('sales.orders.title')} />
        <div className="alert danger">{order.error?.message || 'Not found'}</div>
      </section>
    );
  }

  const o = order.data;
  const customer = (customers.data || []).find((c) => c.id === o.customerId);

  const actions = [
    <Button key="preview" variant="ghost" onClick={() => previewOrderPdf({ order: o, customer, settings: settings.data, t })}>
      {t('pdf.preview')}
    </Button>,
    <Button key="pdf" variant="ghost" onClick={() => downloadOrderPdf({ order: o, customer, settings: settings.data, t })}>
      {t('invoice.print.downloadPdf')}
    </Button>,
  ];
  if (o.status === 'DRAFT') {
    actions.push(
      <Button key="confirm" variant="primary" onClick={() => confirm.mutate()} disabled={confirm.isPending}>
        {t('sales.orders.action.confirm')}
      </Button>,
    );
  }
  // Confirmation document exists from CONFIRMED on - no draft copies to customers.
  if (o.status === 'CONFIRMED' || o.status === 'FULFILLED' || o.status === 'INVOICED') {
    actions.push(
      <Button key="email" variant="ghost" onClick={() => setSendOpen(true)}>
        {t('sales.orders.action.email')}
      </Button>,
    );
  }
  if (o.status === 'CONFIRMED') {
    actions.push(
      <Button key="fulfill" variant="primary" onClick={() => setFulfillTarget(o)}>
        {t('sales.orders.action.fulfill')}
      </Button>,
    );
  }
  if (o.status === 'DRAFT' || o.status === 'CONFIRMED') {
    actions.push(
      <Button key="cancel" variant="danger" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
        {t('sales.orders.action.cancel')}
      </Button>,
    );
  }

  return (
    <section className="page">
      <DetailHeader
        backTo="/sales/orders"
        title={o.orderNumber}
        status={o.status}
        actions={actions}
        metrics={[
          { label: t('detail.metric.total'), value: formatCurrency(o.totalAmount, o.currency) },
          { label: t('detail.metric.date'), value: formatDate(o.orderDate) },
          ...(o.requiredDate
            ? [{ label: t('detail.metric.expected'), value: formatDate(o.requiredDate) }]
            : []),
        ]}
      />

      <Card title={t('detail.header')}>
        <FieldGrid items={[
          { label: t('sales.orders.modal.customer'), value: customer ? `${customer.code} - ${customer.name}` : o.customerId },
          { label: t('col.date'), value: formatDate(o.orderDate) },
          { label: t('sales.orders.modal.requiredDate'), value: formatDate(o.requiredDate) },
          { label: t('sales.orders.col.fulfillment'), value: <Badge status={o.fulfillmentStatus} /> },
          /* Netto, Steuer und Brutto - wie auf der Offerte. Vorher stand hier
             nur ein Betrag, und niemand konnte sehen, ob Steuer darin war. */
          { label: t('vat.net'), value: <span className="mono">{formatCurrency(o.netAmount ?? o.totalAmount, o.currency)}</span> },
          { label: t('vat.tax'), value: <span className="mono">{formatCurrency(o.taxAmount ?? 0, o.currency)}</span> },
          { label: t('col.total'), value: <strong className="mono">{formatCurrency(o.totalAmount, o.currency)}</strong> },
          { label: t('detail.notes'), value: o.notes },
          { label: t('detail.confirmedAt'), value: formatDateTime(o.confirmedAt) },
          { label: t('detail.fulfilledAt'), value: formatDateTime(o.fulfilledAt) },
          { label: t('detail.invoicedAt'), value: formatDateTime(o.invoicedAt) },
        ]} />
      </Card>

      <Card title={t('flow.title')}>
        <DocumentFlow order={o} />
      </Card>

      <Card title={t('detail.lines')}>
        <Table
          rows={o.lines || []}
          getRowKey={(row) => row.id}
          columns={[
            { key: 'description', label: t('detail.lineDescription') },
            { key: 'quantity', label: t('detail.lineQty'), align: 'right', render: (r) => formatQty(t, r.quantity, r.unit) },
            { key: 'fulfilledQuantity', label: t('detail.lineFulfilled'), align: 'right', render: (r) => formatNumber(r.fulfilledQuantity) },
            { key: 'unitPrice', label: t('detail.lineUnitPrice'), align: 'right', render: (r) => formatCurrency(r.unitPrice, o.currency) },
            { key: 'lineAmount', label: t('detail.lineTotal'), align: 'right', render: (r) => formatCurrency(r.lineAmount, o.currency) },
          ]}
        />
      </Card>

      {(deliveries.data || []).length > 0 && (
        <Card title={t('delivery.title')}>
          <Table
            rows={deliveries.data}
            getRowKey={(row) => row.id}
            columns={[
              {
                key: 'deliveryNumber',
                label: t('col.number'),
                render: (r) => <Link to={`/sales/deliveries/${r.id}/print`}>{r.deliveryNumber}</Link>,
              },
              { key: 'deliveryDate', label: t('delivery.deliveryDate'), render: (r) => formatDate(r.deliveryDate) },
              { key: 'carrier', label: t('delivery.carrier'), render: (r) => r.carrier || '-' },
              { key: 'trackingNumber', label: t('delivery.tracking'), render: (r) => r.trackingNumber || '-' },
            ]}
          />
        </Card>
      )}

      <FulfillOrderModal order={fulfillTarget} onClose={() => setFulfillTarget(null)} />
      <OrderSendModal order={sendOpen ? o : null} onClose={() => setSendOpen(false)} />
    </section>
  );
}
